import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import { toast } from 'sonner';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState({});

  useEffect(() => {
    api.get('/audit-logs')
      .then((res) => setLogs(res.data || []))
      .catch(() => toast.error('Error al cargar auditoria'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return logs.filter((log) => {
      if (!normalized) return true;
      return (
        log.actor_name?.toLowerCase().includes(normalized) ||
        log.action?.toLowerCase().includes(normalized) ||
        log.entity_name?.toLowerCase().includes(normalized)
      );
    });
  }, [logs, query]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      const aDate = new Date(a.created_at);
      const bDate = new Date(b.created_at);
      if (aDate < bDate) return -1 * dir;
      if (aDate > bDate) return 1 * dir;
      return 0;
    });
    return copy;
  }, [filtered, sortDir]);

  const allSelected = sorted.length > 0 && sorted.every((l) => selected[l.id]);
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected({});
      return;
    }
    const next = {};
    sorted.forEach((log) => { next[log.id] = true; });
    setSelected(next);
  };
  const toggleRow = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleExport = () => {
    const rows = sorted.filter((l) => selected[l.id]);
    if (!rows.length) {
      toast.error('Selecciona al menos un log');
      return;
    }
    const header = ['actor_name', 'action', 'entity_name', 'created_at'];
    const csv = [
      header.join(','),
      ...rows.map((l) => [l.actor_name, l.action, l.entity_name, l.created_at].map((v) => `"${v || ''}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'audit_logs.csv';
    link.click();
  };

  return (
    <div data-testid="audit-logs-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-4xl font-heading font-bold mb-1">Registro de Auditoria</h1>
          <p className="text-muted-foreground">Seguimiento de acciones del sistema</p>
        </div>
        <Button variant="outline" onClick={handleExport}>Exportar CSV</Button>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Input
            placeholder="Buscar por usuario, accion o entidad"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xs"
          />
          <select
            value={sortDir}
            onChange={(e) => setSortDir(e.target.value)}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="desc">Mas reciente</option>
            <option value="asc">Mas antiguo</option>
          </select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                </th>
                <th className="px-6 py-3 text-left">Usuario</th>
                <th className="px-6 py-3 text-left">Accion</th>
                <th className="px-6 py-3 text-left">Entidad</th>
                <th className="px-6 py-3 text-left">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="px-6 py-4 text-center">Cargando...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan="5" className="px-6 py-4 text-center">No hay registros</td></tr>
              ) : (
                sorted.map((log) => (
                  <tr key={log.id} className="border-t">
                    <td className="px-4 py-4">
                      <input type="checkbox" checked={Boolean(selected[log.id])} onChange={() => toggleRow(log.id)} />
                    </td>
                    <td className="px-6 py-4">{log.actor_name}</td>
                    <td className="px-6 py-4">{log.action}</td>
                    <td className="px-6 py-4">{log.entity_name}</td>
                    <td className="px-6 py-4 text-muted-foreground text-sm">{new Date(log.created_at).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};
export default AuditLogs;
